import { Plus, Trash2, TriangleAlert } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnderlineTab, UnderlineTabs } from "@/components/ui/underline-tabs";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useT } from "@/lib/i18n/use-t";
import type { FlowPage } from "@/lib/types/flow";
import { useGoalContextNav } from "@/pages/clients/detail/flows/instance/flow-wizard/use-goal-context-nav";
import { T } from "./goal-context-view.i18n";
import { decodeGoalNavEntry, encodeGoalNavEntry, type GoalNavEntry } from "./goal-nav-types";
import { splitGoalPagesByPosition } from "./goal-page-position";

const MAX_PORTFOLIOS = 5;

type GoalContextViewProps = {
  goalId: string;
  /**
   * Goal pages (pageType === "goal"), both positions. QL-117 — the view splits them by
   * `goalPosition` around the portfolio dimension: `[before] [portfolios] [after]`.
   */
  goalPages: FlowPage[];
  /** Portfolio pages (pageType === "portfolio"). */
  portfolioPages: FlowPage[];
  /**
   * QL-120 — page-level conditional visibility for goal pages, resolved against the goal this row
   * is drawn for. Both hosts (advisor wizard and public flow) supply it; omitting it leaves every
   * goal page visible.
   */
  isGoalPageVisible?: (page: FlowPage, goalId: string) => boolean;
  /**
   * QL-120 — page-level conditional visibility for portfolio pages. This is the only layer that
   * knows the active portfolio, so portfolio-scoped rules (`__portfolio_type__`,
   * `__portfolio_schedule_type__`) can only be resolved here. Default: every page visible.
   */
  isPortfolioPageVisible?: (page: FlowPage, goalId: string, portfolioId: string) => boolean;
  multiPortfolio: boolean;
  isCompleted: boolean;

  // Portfolio data + actions — supplied by the data wrapper (AdvisorGoalContext).
  portfolios: any[];
  isLoadingPortfolios: boolean;
  isAddingPortfolio?: boolean;
  onAddPortfolio: (name: string, onCreated: (id: string) => void) => void;
  onRemovePortfolio: (portfolioId: string) => void;

  // Content render-props — the host wires these to the concrete renderers.
  renderGoalPage: (page: FlowPage, goalId: string) => React.ReactNode;
  renderPortfolioPage: (page: FlowPage, goalId: string, portfolio: any) => React.ReactNode;

  // Optional hosted globals — present only in the single-goal (flat top row) case.
  globalPagesBefore?: FlowPage[];
  globalPagesAfter?: FlowPage[];
  renderGlobalPage?: (page: FlowPage) => React.ReactNode;

  showValidationErrors?: boolean;
  markers: CompletionMarkers;
};

/**
 * The reusable per-goal navigation panel (QP-150).
 *
 * Draws one goal's heterogeneous row — goal pages plus the portfolio dimension — and switches
 * content by tab. Row order (QL-117): `[globals before] [goal pages before the portfolios]
 * [portfolio tabs | portfolio pages] [goal pages after the portfolios] [globals after]`; an
 * after-page is an ordinary goal page by every other measure (values, rules, markers).
 * Its shape collapses with the flags:
 *  - `multiPortfolio` on  → portfolio-instance tabs (delete) + Add Portfolio; a selected portfolio
 *    reveals an underline sub-nav over the portfolio pages.
 *  - `multiPortfolio` off → the single portfolio's pages render inline as tabs.
 *  - hosted globals (single-goal) → the global tabs bracket the row, producing one flat top row.
 *
 * Visual rule: the row is boxed pills when it hosts globals (top level) or contains portfolio
 * instances (multiPortfolio); otherwise (nested + single-portfolio) it is an underline nav. The
 * innermost portfolio-page sub-nav is always underline. Layout only — no data fetching, no business
 * logic; content flows through the three render-props.
 */
export function GoalContextView({
  goalId,
  goalPages,
  portfolioPages,
  isGoalPageVisible,
  isPortfolioPageVisible,
  multiPortfolio,
  isCompleted,
  portfolios,
  isLoadingPortfolios,
  isAddingPortfolio = false,
  onAddPortfolio,
  onRemovePortfolio,
  renderGoalPage,
  renderPortfolioPage,
  globalPagesBefore,
  globalPagesAfter,
  renderGlobalPage,
  showValidationErrors,
  markers,
}: GoalContextViewProps) {
  const t = useT(T);

  const hostsGlobals = renderGlobalPage != null;
  const globalsBefore = globalPagesBefore ?? [];
  const globalsAfter = globalPagesAfter ?? [];

  // The row is boxed pills at the top level (hosting globals) or when it carries portfolio
  // instances; otherwise it's an underline page-picker (nested + single portfolio).
  const boxedRow = hostsGlobals || multiPortfolio;

  // Global-page selection sits outside the per-goal hook. Default to the first globals-before page
  // when present (preserves today's "open on the leading global page" behavior).
  const [selectedGlobalPageId, setSelectedGlobalPageId] = useState<string | null>(
    globalsBefore.length > 0 ? `before:${globalsBefore[0].id}` : null,
  );

  // QP-172 — when a pending portfolio navigation is consumed by the hook,
  // clear the global-page selection so the portfolio content shows.
  const handlePendingNavigated = useCallback(() => {
    setSelectedGlobalPageId(null);
  }, []);

  const singlePortfolioId: string | null = portfolios[0]?.id ?? null;

  // QL-120 — page-level conditions resolve per goal (and, for portfolio pages, per portfolio).
  const visibleGoalPages = useMemo(
    () => (isGoalPageVisible ? goalPages.filter((p) => isGoalPageVisible(p, goalId)) : goalPages),
    [goalPages, goalId, isGoalPageVisible],
  );
  // QL-117 — the row draws the goal pages around the portfolio dimension. Memoised: both lists
  // feed the nav hook's effects.
  const { before: visibleGoalPagesBefore, after: visibleGoalPagesAfter } = useMemo(
    () => splitGoalPagesByPosition(visibleGoalPages),
    [visibleGoalPages],
  );

  const portfolioPagesFor = useCallback(
    (portfolioId: string | null) => {
      if (!isPortfolioPageVisible || !portfolioId) return portfolioPages;
      return portfolioPages.filter((p) => isPortfolioPageVisible(p, goalId, portfolioId));
    },
    [portfolioPages, goalId, isPortfolioPageVisible],
  );

  // The single-portfolio row lists portfolio pages directly against `portfolios[0]`, and that is
  // also the only portfolio the nav hook can land on in that mode — so this list is what both the
  // hook and the row need. In multi-portfolio mode the hook only ever selects a portfolio
  // *instance*, never a portfolio page, so the unfiltered list is safe there.
  const navPortfolioPages = useMemo(
    () => (multiPortfolio ? portfolioPages : portfolioPagesFor(singlePortfolioId)),
    [multiPortfolio, portfolioPages, portfolioPagesFor, singlePortfolioId],
  );

  const {
    activeEntry,
    activePortfolioId,
    activePortfolioPageIndex,
    selectEntry,
    setActivePortfolioPageIndex,
  } = useGoalContextNav({
    goalId,
    goalPagesBefore: visibleGoalPagesBefore,
    goalPagesAfter: visibleGoalPagesAfter,
    portfolioPages: navPortfolioPages,
    portfolios,
    isLoadingPortfolios,
    multiPortfolio,
    onPendingNavigated: handlePendingNavigated,
  });

  // Portfolio delete confirmation (popup — mirrors the goal delete dialog).
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<string | null>(null);

  const selectedGlobalPage = useMemo(() => {
    if (!selectedGlobalPageId) return null;
    const [type, id] = selectedGlobalPageId.split(":");
    if (type === "before") return globalsBefore.find((p) => p.id === id) ?? null;
    if (type === "after") return globalsAfter.find((p) => p.id === id) ?? null;
    return null;
  }, [selectedGlobalPageId, globalsBefore, globalsAfter]);

  const activePortfolio = multiPortfolio
    ? (portfolios.find((p: { id: string }) => p.id === activePortfolioId) ?? null)
    : (portfolios[0] ?? null);

  // The portfolio-instance sub-nav (L3) runs inside a selected portfolio, so it filters against
  // that portfolio rather than the first one.
  const activePortfolioPages = useMemo(
    () => (multiPortfolio ? portfolioPagesFor(activePortfolioId) : navPortfolioPages),
    [multiPortfolio, portfolioPagesFor, activePortfolioId, navPortfolioPages],
  );

  // When a portfolio is deleted, the guard in useGoalContextNav snaps activePortfolioId to the
  // first remaining portfolio but leaves activeEntry stale (pointing to the deleted portfolio).
  // Detect this divergence and use activePortfolioId for the tab value so the correct tab is
  // highlighted.
  const activeTabValue = selectedGlobalPage
    ? (selectedGlobalPageId as string)
    : activeEntry
      ? activeEntry.kind === "portfolio" &&
        activePortfolioId &&
        activeEntry.portfolioId !== activePortfolioId
        ? `pf:${activePortfolioId}`
        : encodeGoalNavEntry(activeEntry)
      : "";

  const handleTabChange = (value: string) => {
    const prefix = value.slice(0, value.indexOf(":"));
    if (prefix === "before" || prefix === "after") {
      setSelectedGlobalPageId(value);
      return;
    }
    setSelectedGlobalPageId(null);
    const entry = decodeGoalNavEntry(value);
    if (entry) selectEntry(entry);
  };

  const handleAddPortfolio = () => {
    if (portfolios.length >= MAX_PORTFOLIOS) {
      toast.error(t.maxPortfolios);
      return;
    }
    onAddPortfolio(`Portfolio ${portfolios.length + 1}`, (id) =>
      selectEntry({ kind: "portfolio", portfolioId: id }),
    );
  };

  const handleRemovePortfolio = (portfolioId: string) => {
    if (portfolios.length <= 1) {
      toast.error(t.cannotDeleteLast);
      return;
    }
    setPortfolioToDelete(portfolioId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmRemovePortfolio = () => {
    if (portfolioToDelete) onRemovePortfolio(portfolioToDelete);
    setPortfolioToDelete(null);
  };

  // --- Row items (goal pages + portfolio dimension), shared by boxed + underline renderers ---
  const goalPageError = (pageId: string) =>
    !!(showValidationErrors && markers.missingGoalPageKeys.has(`${goalId}:${pageId}`));
  const portfolioPageError = (portfolioId: string | null, pageId: string) =>
    !!(
      showValidationErrors &&
      portfolioId &&
      markers.missingPortfolioPageKeys.has(`${portfolioId}:${pageId}`)
    );

  const isEntryActive = (entry: GoalNavEntry) =>
    !selectedGlobalPage && activeTabValue === encodeGoalNavEntry(entry);

  const renderRow = () => {
    // Goal-page triggers are drawn twice per row (before and after the portfolio dimension).
    const goalPageTriggers = (pages: FlowPage[]) =>
      pages.map((page) => (
        <TabsTrigger
          key={page.id}
          value={`gp:${page.id}`}
          className="shrink-0 flex items-center justify-center gap-1.5"
        >
          {page.name}
          {goalPageError(page.id) && <TriangleAlert className="h-3.5 w-3.5 text-destructive" />}
        </TabsTrigger>
      ));
    const goalPageUnderlineTabs = (pages: FlowPage[]) =>
      pages.map((page) => (
        <UnderlineTab
          key={page.id}
          label={page.name}
          active={isEntryActive({ kind: "goalPage", pageId: page.id })}
          hasError={goalPageError(page.id)}
          onClick={() => handleTabChange(`gp:${page.id}`)}
        />
      ));

    if (boxedRow) {
      return (
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex-1">
            <Tabs value={activeTabValue} onValueChange={handleTabChange}>
              <TabsList className="flex w-fit max-w-full justify-start gap-1">
                {hostsGlobals &&
                  globalsBefore.map((page) => (
                    <TabsTrigger
                      key={page.id}
                      value={`before:${page.id}`}
                      className="shrink-0 flex items-center justify-center gap-1.5"
                    >
                      {page.name}
                      {showValidationErrors && markers.missingPageIds.has(page.id) && (
                        <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                      )}
                    </TabsTrigger>
                  ))}

                {goalPageTriggers(visibleGoalPagesBefore)}

                {multiPortfolio
                  ? portfolios.map((p: { id: string; name?: string }) => {
                      const hasError = !!(
                        showValidationErrors && markers.missingPortfolioIds.has(p.id)
                      );
                      return (
                        <TabsTrigger
                          key={p.id}
                          value={`pf:${p.id}`}
                          className="shrink-0 flex items-center gap-2"
                        >
                          <span className="truncate max-w-[12rem]">
                            {p.name || t.portfolioFallback}
                          </span>
                          {hasError && (
                            <TriangleAlert className="h-3.5 w-3.5 text-destructive shrink-0" />
                          )}
                          {!isCompleted && portfolios.length > 1 && (
                            // biome-ignore lint/a11y/useSemanticElements: nested inside a TabsTrigger button
                            <span
                              role="button"
                              className="inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted cursor-pointer transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleRemovePortfolio(p.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleRemovePortfolio(p.id);
                                }
                              }}
                              aria-label={`${t.removePortfolio} ${p.name || ""}`}
                              tabIndex={-1}
                            >
                              <Trash2 className="h-3 w-3" />
                            </span>
                          )}
                        </TabsTrigger>
                      );
                    })
                  : navPortfolioPages.map((page) => (
                      <TabsTrigger
                        key={page.id}
                        value={`pp:${page.id}`}
                        className="shrink-0 flex items-center justify-center gap-1.5"
                      >
                        {page.name}
                        {portfolioPageError(singlePortfolioId, page.id) && (
                          <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                        )}
                      </TabsTrigger>
                    ))}

                {goalPageTriggers(visibleGoalPagesAfter)}

                {hostsGlobals &&
                  globalsAfter.map((page) => (
                    <TabsTrigger
                      key={page.id}
                      value={`after:${page.id}`}
                      className="shrink-0 flex items-center justify-center gap-1.5"
                    >
                      {page.name}
                      {showValidationErrors && markers.missingPageIds.has(page.id) && (
                        <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                      )}
                    </TabsTrigger>
                  ))}
              </TabsList>
            </Tabs>
          </div>
          {multiPortfolio && !isCompleted && portfolios.length < MAX_PORTFOLIOS && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddPortfolio}
              disabled={isAddingPortfolio}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t.addPortfolio}
            </Button>
          )}
        </div>
      );
    }

    // Underline row (nested + single portfolio): goal pages + portfolio pages as a page-picker,
    // with the after-portfolio goal pages closing the row.
    return (
      <UnderlineTabs>
        {goalPageUnderlineTabs(visibleGoalPagesBefore)}
        {navPortfolioPages.map((page) => {
          const active = isEntryActive({ kind: "portfolioPageDirect", pageId: page.id });
          return (
            <UnderlineTab
              key={page.id}
              label={page.name}
              active={active}
              hasError={portfolioPageError(singlePortfolioId, page.id)}
              onClick={() => handleTabChange(`pp:${page.id}`)}
            />
          );
        })}
        {goalPageUnderlineTabs(visibleGoalPagesAfter)}
      </UnderlineTabs>
    );
  };

  const renderContent = () => {
    if (selectedGlobalPage && renderGlobalPage) return renderGlobalPage(selectedGlobalPage);
    if (!activeEntry) return null;

    if (activeEntry.kind === "goalPage") {
      const page = visibleGoalPages.find((p) => p.id === activeEntry.pageId);
      // Keyed by goal so switching goals remounts the page content and resets any
      // nested local state (e.g. tabs_block tab selection).
      return page ? <Fragment key={goalId}>{renderGoalPage(page, goalId)}</Fragment> : null;
    }

    if (activeEntry.kind === "portfolioPageDirect") {
      const page = navPortfolioPages.find((p) => p.id === activeEntry.pageId);
      if (!page) return null;
      if (!activePortfolio) {
        return <div className="text-sm text-muted-foreground">{t.loading}</div>;
      }
      return (
        <Fragment key={activePortfolio.id}>
          {renderPortfolioPage(page, goalId, activePortfolio)}
        </Fragment>
      );
    }

    // Portfolio instance selected — show its portfolio-page sub-nav + the active page.
    if (isLoadingPortfolios && !activePortfolio) {
      return <div className="text-sm text-muted-foreground">{t.loading}</div>;
    }
    if (!activePortfolio) {
      return <div className="text-sm text-muted-foreground">{t.loading}</div>;
    }
    // The visible list can shrink under the current index (a rule hid the open page), so clamp
    // rather than fall back silently — otherwise the sub-nav highlights nothing.
    const pageIndex =
      activePortfolioPageIndex < activePortfolioPages.length ? activePortfolioPageIndex : 0;
    const page = activePortfolioPages[pageIndex] ?? null;
    return (
      <div className="space-y-4">
        {activePortfolioPages.length > 1 && (
          <UnderlineTabs>
            {activePortfolioPages.map((p, index) => (
              <UnderlineTab
                key={p.id}
                label={p.name}
                active={pageIndex === index}
                hasError={portfolioPageError(activePortfolioId, p.id)}
                onClick={() => setActivePortfolioPageIndex(index)}
              />
            ))}
          </UnderlineTabs>
        )}
        {/* Keyed by portfolio so switching portfolio tabs remounts the page content and
            resets any nested local state (QPBI-322 — tabs_block kept its active tab). */}
        {page && (
          <Fragment key={activePortfolio.id}>
            {renderPortfolioPage(page, goalId, activePortfolio)}
          </Fragment>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderRow()}
      <div className="space-y-6">{renderContent()}</div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deletePortfolioTitle}
        description={t.deletePortfolioDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleConfirmRemovePortfolio}
        variant="destructive"
      />
    </div>
  );
}
